import { CSSResultGroup, html, LitElement, nothing, PropertyValues, TemplateResult } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { rmaOverview } from './overview.style.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { breakpoints } from '@cloudsuite/mosaic-shared/styles/breakpoints.js'
import { BREAKPOINTS, ConcreteBreakpoints } from '@cloudsuite/mosaic-shared/scripts/breakpoints.js'
import { WINDOW_LOCATION, WindowLocation } from '@cloudsuite/mosaic-shared/scripts/window-location.js'
import { redirectToPath } from '@cloudsuite/mosaic-shared/scripts/base-url.js'
import { parseRmaOverviewDTO, RmaOverviewDTO, RmaOverviewItemDTO } from 'components/ordering/rma/api/dtos/rma-overview-dto.js'
import { executeRmaRequest } from 'components/ordering/rma/api/executor.js'
import { GetRmaOverview } from 'components/ordering/rma/api/requests/get-rma-overview.js'
import { rmaBadgeVariant } from 'components/ordering/rma/components/shared/badge-variant.js'
import 'components/shared/layout/table.js'
import 'components/shared/pagination/pagination.js'
import { PaginationOnNavigateEvent } from 'components/shared/pagination/pagination.js'

initializeIconsByRole({
  'csc-rma-overview.action-view': { name: 'eye', style: 'solid' },
})

const NO_DATE = '-'

const pageNumberFromLink = (link: string): number => {
  const query = link.includes('?') ? link.slice(link.indexOf('?')) : ''
  const page = Number(new URLSearchParams(query).get('page'))
  return Number.isFinite(page) && page > 0 ? page : 1
}

type RmaOverviewRowView = {
  number: string | null
  sent: string | null
  userCell: { name: string; email: string | null }
  statusCell: { state: string; status: string; lastUpdate: string | null }
  rmaId: string
}

const translations = {
  headerRma: () => t('header_rma', { ns: 'csc-rma-overview', default: 'RMA' }),
  headerCreated: () => t('header_created', { ns: 'csc-rma-overview', default: 'Created' }),
  headerUser: () => t('header_user', { ns: 'csc-rma-overview', default: 'User' }),
  headerStatus: () => t('header_status', { ns: 'csc-rma-overview', default: 'Status' }),
  lastUpdate: () => t('last_update', { ns: 'csc-rma-overview', default: 'Last update' }),
  actionView: () => t('action_view', { ns: 'csc-rma-overview', default: 'View RMA' }),
  emptyTitle: () => t('empty_title', { ns: 'csc-rma-overview', default: 'There are no return requests found' }),
  emptyDescription: () => t('empty_description', { ns: 'csc-rma-overview', default: 'Start a new return request by clicking the button at the top of the page' }),
  openDraftTitle: () => t('open_draft_title', { ns: 'csc-rma-overview', default: 'Open return request' }),
  openDraftDescription: () => t('open_draft_description', { ns: 'csc-rma-overview', default: 'You still have an open return request. Please complete this before you can submit a new request.' }),
  openDraftAction: () => t('open_draft_action', { ns: 'csc-rma-overview', default: 'Continue return request' }),
} as const

@customElement('csc-rma-overview')
export class CSCRmaOverview extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, rmaOverview]

  @property({ attribute: 'rmas', type: String })
  public rmas = ''

  // Only used when the list is fetched instead of injected; csc-rma passes the
  // page it read from the URL.
  @property({ attribute: 'page', type: Number })
  public page = 1

  @property({ attribute: 'locale', type: String })
  public locale = 'en'

  @property({ attribute: 'has-open-draft', type: Boolean })
  public hasOpenDraft = false

  @property({ attribute: 'draft-rma-url', type: String })
  public draftRmaUrl = ''

  @state()
  private overview: RmaOverviewDTO | null = null

  @state()
  private onSmallDevice = false

  @inject(BREAKPOINTS)
  private breakpoints: ConcreteBreakpoints | undefined

  @inject(WINDOW_LOCATION)
  private windowLocation: WindowLocation | undefined

  protected override willUpdate(changed: PropertyValues): void {
    super.willUpdate(changed)
    // An absent rmas attribute never shows up in changed, so the first update has
    // to be handled explicitly for the fetch path to run at all.
    if (!this.hasUpdated || changed.has('rmas')) {
      try {
        this.overview = this.rmas ? parseRmaOverviewDTO(JSON.parse(this.rmas)) : null
      } catch (error) {
        console.error('[csc-rma-overview] failed to parse rmas attribute:', error)
        this.overview = null
      }
      if (!this.rmas) void this.fetchOverview()
    }
  }

  // The server-rendered pages inject the list as the rmas attribute. When it is
  // absent — csc-rma routing to the overview itself — read it from the API.
  private async fetchOverview(): Promise<void> {
    const result = await executeRmaRequest(new GetRmaOverview(this.page))
    if (result instanceof RmaOverviewDTO) this.overview = result
  }

  override connectedCallback() {
    super.connectedCallback()
    if (this.breakpoints) {
      const bp = this.breakpoints
      createEffect(() => {
        this.onSmallDevice = bp.small()
      })
      this.breakpoints.matchMedia(breakpoints.medium)
    }
  }

  private formatDate(value: string | null): string {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    return new Intl.DateTimeFormat(this.locale, { dateStyle: 'long' }).format(date)
  }

  private toRowView(item: RmaOverviewItemDTO): RmaOverviewRowView {
    return {
      number: item.number,
      sent: item.sent,
      userCell: { name: item.user, email: item.userEmail },
      statusCell: { state: item.state, status: item.status, lastUpdate: item.lastUpdate },
      rmaId: item.rmaId,
    }
  }

  private get columns() {
    return [
      { name: translations.headerRma(), key: 'number', width: 'auto' },
      { name: translations.headerCreated(), key: 'sent', width: 'auto', cellRenderer: this.dateRenderer },
      { name: translations.headerUser(), key: 'userCell', width: 'auto', cellRenderer: this.userRenderer },
      { name: translations.headerStatus(), key: 'statusCell', width: 'auto', cellRenderer: this.statusRenderer },
      { name: '', key: 'rmaId', width: 'min-content', cellRenderer: this.actionRenderer },
    ]
  }

  private dateRenderer = (value: string | null) => this.formatDate(value)

  private userRenderer = (cell: { name: string; email: string }) => html`
    <cse-text>${cell.name}</cse-text>
    <cse-text variant="caption-m">${cell.email}</cse-text>
  `

  private statusBadge = (cell: { state: string; status: string }) => html`
    <cse-badge data-test="rma-status-badge" size="small" variant=${rmaBadgeVariant(cell.state)}>${cell.status}</cse-badge>
  `

  private statusRenderer = (cell: { state: string; status: string; lastUpdate: string | null }) => html`
    ${this.statusBadge(cell)}
    <cse-text variant="caption-m">${translations.lastUpdate()}: ${this.formatDate(cell.lastUpdate) || NO_DATE}</cse-text>
  `

  private actionRenderer = (rmaId: string) => html`
    <cse-chip-group>
      <cse-chip data-test="rma-action-view" @click=${() => this.actionOpen(rmaId)} title=${translations.actionView()}>
        <cse-icon icon-role="csc-rma-overview.action-view" slot="prefix"></cse-icon>
        ${this.onSmallDevice ? translations.actionView() : nothing}
      </cse-chip>
    </cse-chip-group>
  `

  private actionOpen(rmaId: string) {
    if (this.windowLocation) {
      redirectToPath(this.windowLocation, `account/rma/detail/${rmaId}/`)
    }
  }

  private renderOpenDraftBanner() {
    if (!this.hasOpenDraft) return nothing
    return html`
      <cse-alert data-test="rma-open-draft-banner" variant="warning">
        ${translations.openDraftTitle()}
        <span slot="description">${translations.openDraftDescription()}</span>
        ${this.draftRmaUrl
          ? html`<cse-button slot="link" data-test="rma-open-draft-link" url=${this.draftRmaUrl} size="small">${translations.openDraftAction()}</cse-button>`
          : nothing}
      </cse-alert>
    `
  }

  private renderEmptyState() {
    return html`
      <cse-alert data-test="rma-overview-empty" variant="info">
        ${translations.emptyTitle()}
        <span slot="description">${translations.emptyDescription()}</span>
      </cse-alert>
    `
  }

  private renderMobileCard(row: RmaOverviewRowView): TemplateResult {
    return html`
      <cse-selectable data-test="rma-overview-row" class="rma-overview__card">
        <div class="rma-overview__card-info">
          <cse-text>${row.number}</cse-text>
          <div class="rma-overview__card-meta">
            <cse-text variant="caption-l">${this.formatDate(row.sent)}</cse-text>
            ${this.statusBadge(row.statusCell)}
          </div>
        </div>
        <div class="rma-overview__card-user">
          <cse-text>${row.userCell.name}</cse-text>
          <cse-text variant="caption-m">${row.userCell.email}</cse-text>
        </div>
        ${this.actionRenderer(row.rmaId)}
      </cse-selectable>
    `
  }

  private renderTable(overview: RmaOverviewDTO) {
    const items = overview.items.filter((item) => item.state !== 'draft')
    if (!items.length) return this.renderEmptyState()
    const rows = items.map((item) => this.toRowView(item))
    if (this.onSmallDevice) {
      return html`<div class="rma-overview__list">${rows.map((row) => this.renderMobileCard(row))}</div>`
    }
    return html`<csc-table .columns=${this.columns} .data=${rows} variant="secondary"></csc-table>`
  }

  private renderPagination(overview: RmaOverviewDTO) {
    const { paginator } = overview
    if (paginator.numPages === 0 || overview.items.length === 0) return nothing
    return html`
      <csc-pagination
        current-page=${paginator.page}
        number-of-pages=${paginator.numPages}
        first-link=${paginator.firstPageLink}
        prev-link=${paginator.prevPageLink}
        next-link=${paginator.nextPageLink}
        last-link=${paginator.lastPageLink}
        @pagination-on-navigate=${this.handlePageNavigation}
      ></csc-pagination>
    `
  }

  // csc-pagination cancels the click and leaves the navigating to its host.
  private handlePageNavigation(event: PaginationOnNavigateEvent): void {
    const link = event.pageLink
    if (!link) return

    // Fetching our own list means we can swap pages without a page load; a
    // server-rendered list has to navigate so the backend serves the new page.
    if (!this.rmas) {
      this.page = pageNumberFromLink(link)
      void this.fetchOverview()
      return
    }

    this.windowLocation?.redirect(new URL(link, window.location.href))
  }

  override render() {
    if (!this.overview) return html`<div class="rma-overview" data-test="rma-overview"></div>`
    return html`
      <div class="rma-overview" data-test="rma-overview">
        ${this.renderOpenDraftBanner()}
        ${this.renderTable(this.overview)}
        ${this.renderPagination(this.overview)}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-rma-overview': CSCRmaOverview
  }
}
